import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { SmsTemplate } from "@/types/sms-queue";
import { useActiveStoreId } from "./campaigns/useActiveStoreId";
import { useAuth } from "@/context/AuthContext";

export const useSmsTemplates = () => {
  const queryClient = useQueryClient();
  const { data: storeId } = useActiveStoreId();
  const { user } = useAuth();

  const templatesQuery = useQuery({
    queryKey: ["sms-templates", storeId, user?.id],
    queryFn: async (): Promise<SmsTemplate[]> => {
      if (!user?.id) {
        console.log('No user found, returning empty templates list');
        return [];
      }

      let query = supabase
        .from("sms_templates")
        .select("*")
        .eq("user_id", user.id);

      // Se há uma loja ativa, filtrar por ela, senão buscar templates sem loja
      if (storeId) {
        query = query.eq("store_id", storeId);
      } else {
        query = query.is("store_id", null);
      }

      const { data, error } = await query.order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  const createTemplate = useMutation({
    mutationFn: async (template: Omit<SmsTemplate, "id" | "created_at" | "user_id" | "store_id">) => {
      if (!user?.id) {
        throw new Error("Usuário não autenticado");
      }

      const { data, error } = await supabase
        .from("sms_templates")
        .insert([{
          ...template,
          user_id: user.id,
          store_id: storeId || null // Permite criar template sem loja durante onboarding
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-templates", storeId, user?.id] });
      toast.success("Template criado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao criar template:", error);
      toast.error("Erro ao criar template");
    },
  });

  const updateTemplate = useMutation({
    mutationFn: async ({ id, ...template }: Partial<SmsTemplate> & { id: string }) => {
      const { data, error } = await supabase
        .from("sms_templates")
        .update(template)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-templates", storeId, user?.id] });
      toast.success("Template atualizado com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao atualizar template:", error);
      toast.error("Erro ao atualizar template");
    },
  });

  const deleteTemplate = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sms_templates")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-templates", storeId, user?.id] });
      toast.success("Template excluído com sucesso!");
    },
    onError: (error) => {
      console.error("Erro ao excluir template:", error);
      toast.error("Erro ao excluir template");
    },
  });

  const setDefaultTemplate = useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) throw new Error('Usuário não autenticado');
      
      // Primeiro desmarcar todos os templates do usuário como não-padrão
      // Considerar tanto templates com loja quanto sem loja
      let clearQuery = supabase
        .from('sms_templates')
        .update({ is_default: false })
        .eq('user_id', user.id);

      if (storeId) {
        clearQuery = clearQuery.eq('store_id', storeId);
      } else {
        clearQuery = clearQuery.is('store_id', null);
      }
      
      const { error: clearError } = await clearQuery;
      if (clearError) throw clearError;
      
      // Depois marcar o template escolhido como padrão
      const { data, error } = await supabase
        .from("sms_templates")
        .update({ is_default: true })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sms-templates", storeId, user?.id] });
      toast.success("Template definido como padrão!");
    },
    onError: (error) => {
      console.error("Erro ao definir template padrão:", error);
      toast.error("Erro ao definir template padrão");
    },
  });

  return {
    templates: templatesQuery.data || [],
    isLoading: templatesQuery.isLoading,
    error: templatesQuery.error,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    setDefaultTemplate,
  };
};